import { useFormHandling } from '@pelckmans/business-components/hooks/useFormHandling';
import Button from '@pelckmans/elementis/components/button';
import HighlightedItem from '@pelckmans/elementis/components/highlighted-item';
import TextField from '@pelckmans/elementis/components/text-field';
import { useToast } from '@pelckmans/elementis/components/toast';
import { AxiosError } from 'axios';
import { FormEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Group, linkToGroup, Member } from '../../../../../../api/groups';
import { TOAST_ID } from '../../../../../../constants/ToastIds';
import { validateEmail } from '../../../../../../utils/emailValidator';
import { SchoolYear } from '../../../../context/MyGroupsContext';
import useGroups from '../../../../hooks/useGroups';

type FormValues = {
  email: string;
};

interface Props {
  group: Group;
  members: Member[];
  schoolYear: SchoolYear;
  setMembers: (members: Member[]) => void;
}

const initialFormValues: FormValues = {
  email: '',
};

function InviteStudent({ group, members, setMembers, schoolYear }: Props) {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const { updateCounters } = useGroups();
  const { addToast } = useToast();

  const { invitationId } = group;
  // TODO remove after Ianua full launch
  const isIanua = window.location.pathname.includes('/ianua');
  const activationLinkUrl = `${window.location.origin}/${isIanua ? 'ianua/' : ''}invite/${invitationId}`;

  const validateForm = useCallback(
    (values: FormValues) => {
      const result: Record<string, string> = {};

      const { email } = values;

      if (email?.length === 0 || !validateEmail(email)) {
        result.email = t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.INVITE_EMAIL.INPUT.ERROR');
      }

      return result;
    },
    [t],
  );

  const { errors, handleChange, handleBlur, formValues, isFormValid, resetForm } = useFormHandling<FormValues>(
    initialFormValues,
    validateForm,
  );

  const linkStudentToGroup = async (email: string, selectedSchoolYear: string, existingInvitation?: Member) => {
    try {
      const linkedStudent = await linkToGroup(selectedSchoolYear, group.id, email);
      addToast({
        id: TOAST_ID.LINK_STUDENT_SUCCESS,
        variant: 'success',
        title: t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.INVITE_EMAIL.TOAST.SUCCESS.TITLE'),
        children: t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.INVITE_EMAIL.TOAST.SUCCESS.MESSAGE', {
          email,
        }),
      });

      if (existingInvitation) {
        const nextMembers = members.filter(m => m.primaryEmail !== existingInvitation.primaryEmail);
        const newMembers = [...nextMembers, linkedStudent];
        setMembers(newMembers);
      } else {
        const newMembers = [...members, linkedStudent];
        setMembers(newMembers);
      }

      if (linkedStudent.status === 'accepted' && existingInvitation?.status === 'pending') {
        updateCounters(group.id, {
          totalAccepted: group.totalAccepted + 1,
          totalPending: group.totalPending - 1,
        });

        return;
      }

      updateCounters(group.id, {
        totalPending: existingInvitation?.status === 'pending' ? group.totalPending : group.totalPending + 1,
        totalDeclined: existingInvitation?.status === 'declined' ? group.totalDeclined - 1 : group.totalDeclined,
      });
      setSubmitted(false);
    } catch (e) {
      if ((e as AxiosError).response?.status === 404) {
        addToast({
          id: TOAST_ID.STUDENT_INVITE_EMAIL_NOT_FOUND,
          variant: 'warning',
          title: t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.INVITE_EMAIL.TOAST.NOT_FOUND.TITLE'),
          children: t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.INVITE_EMAIL.TOAST.NOT_FOUND.MESSAGE'),
        });
      } else {
        addToast({
          id: TOAST_ID.STUDENT_INVITE_ERROR,
          variant: 'danger',
          title: t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.INVITE_EMAIL.TOAST.ERROR.TITLE'),
          children: t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.INVITE_EMAIL.TOAST.ERROR.MESSAGE'),
        });
        setSubmitted(false);
      }
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);

    if (!isFormValid) return;
    if (!schoolYear) return;

    const existingInvitation = members.find(m => m.primaryEmail === formValues.email);

    if (existingInvitation?.status === 'pending') {
      addToast({
        id: TOAST_ID.STUDENT_INVITE_ALREADY_INVITED,
        variant: 'warning',
        title: t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.INVITE_EMAIL.TOAST.ALREADY_INVITED.TITLE'),
        children: t(
          'PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.INVITE_EMAIL.TOAST.ALREADY_INVITED.MESSAGE',
          { email: formValues.email },
        ),
      });
      setSubmitted(false);
      return resetForm();
    }

    if (existingInvitation?.status === 'accepted') {
      addToast({
        id: TOAST_ID.STUDENT_INVITE_ALREADY_ACCEPTED,
        variant: 'warning',
        title: t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.INVITE_EMAIL.TOAST.ALREADY_ACCEPTED.TITLE'),
        children: t(
          'PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.INVITE_EMAIL.TOAST.ALREADY_ACCEPTED.MESSAGE',
          { email: formValues.email },
        ),
      });
      setSubmitted(false);
      return resetForm();
    }

    await linkStudentToGroup(formValues.email, schoolYear.id, existingInvitation);

    return resetForm();
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(activationLinkUrl);
    addToast({
      id: TOAST_ID.COPIED_INVITE_LINK,
      variant: 'success',
      title: t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.INVITE_LINK.TOAST.SUCCESS.TITLE'),
      children: t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.INVITE_LINK.TOAST.SUCCESS.MESSAGE'),
    });
  };

  return (
    <>
      <HighlightedItem
        variant="input"
        description={t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.INVITE_LINK.DESCRIPTION')}
        inputProps={{
          value: activationLinkUrl,
          'aria-label': t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.INVITE_LINK.INPUT.A11Y_LABEL'),
          required: true,
          disabled: true,
          name: 'invite-link',
          id: 'invite-link',
          type: 'text',
          readOnly: true,
          buttonGroupProps: {
            'aria-label': t(
              'PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.INVITE_LINK.INPUT.ACTION_GROUP_A11Y_LABEL',
            ),
            children: [
              <Button key="copy" as="button" prominence="neutral" onClick={copyToClipboard}>
                {t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.INVITE_LINK.INPUT.ACTION_COPY')}
              </Button>,
            ],
            role: 'group',
            size: 'sm',
          },
        }}
      />
      <form onSubmit={handleSubmit} noValidate>
        <TextField
          id="email"
          name="email"
          value={formValues.email}
          onChange={handleChange}
          onBlur={handleBlur}
          aria-label={t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.INVITE_EMAIL.INPUT.A11Y_LABEL')}
          placeholder={t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.INVITE_EMAIL.INPUT.PLACEHOLDER')}
          type="email"
          error={!!errors.email && submitted}
          errorText={errors.email}
          required
          buttonGroupProps={{
            'aria-label': t(
              'PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.INVITE_EMAIL.INPUT.ACTION_GROUP_A11Y_LABEL',
            ),
            children: [
              <Button key="invite" type="submit" as="button" prominence="secondary">
                {t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.STUDENTS.BODY.INVITE_EMAIL.INPUT.ACTION_SEND_INVITE')}
              </Button>,
            ],
            role: 'group',
            size: 'sm',
          }}
        />
      </form>
    </>
  );
}

export default InviteStudent;
